import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import {
  getPlacementData, addStudentToPlacement, updatePlacementStage,
  removeStudentFromPlacement, getStudents,
} from '../../lib/api';
import type { PlacementStudent, PlacementStage, StudentFull } from '../../lib/api';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

const STAGES = [
  'Face-to-Face Interview',
  'Project 1', 'Project 2', 'Project 3', 'Project 4',
  'Second-Round Interview', 'Third-Round Interview',
];

const IS_PROJECT = (i: number) => i >= 1 && i <= 4;

const STATUS_CFG: Record<string, { label: string; bg: string; text: string; dot: string; ring: string }> = {
  pending:     { label: 'Pending',     bg: '#f3f4f6', text: '#6b7280', dot: '#d1d5db', ring: '#e5e7eb' },
  in_progress: { label: 'In Progress', bg: '#eff6ff', text: '#1d4ed8', dot: '#3b82f6', ring: '#bfdbfe' },
  completed:   { label: 'Completed',   bg: '#dcfce7', text: '#15803d', dot: '#22c55e', ring: '#bbf7d0' },
  failed:      { label: 'Failed',      bg: '#fee2e2', text: '#b91c1c', dot: '#ef4444', ring: '#fecaca' },
};

// Offers recorded on the Job Board (an application marked "Offer Received")
const lakhs = (n: number) => `₹${(n / 100000).toFixed(1)}L`;
function offerSummary(s: PlacementStudent): string | null {
  const offers = s.jobOffers ?? [];
  if (offers.length === 0) return null;
  if (offers.length === 1) return `Offer · ${offers[0].company || offers[0].jobTitle}${offers[0].salary ? ` · ${lakhs(offers[0].salary)}` : ''}`;
  const best = Math.max(...offers.map(o => o.salary || 0));
  return `${offers.length} offers${best ? ` · best ${lakhs(best)}` : ''}`;
}

function currentStageOf(s: PlacementStudent): string {
  const first = s.stages.find(st => st.status !== 'completed');
  if (!first) return 'All Complete';
  return first.stageName;
}
function completedCount(s: PlacementStudent) { return s.stages.filter(st => st.status === 'completed').length; }
function hasFailed(s: PlacementStudent) { return s.stages.some(st => st.status === 'failed'); }

// ── INLINE STYLES ─────────────────────────────────────────────────────────────

const CSS = `
@keyframes ptFadeUp {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes ptPop {
  0%   { transform: scale(0.92); opacity: 0; }
  60%  { transform: scale(1.03); }
  100% { transform: scale(1);    opacity: 1; }
}
@keyframes ptBarFill {
  from { width: 0; }
}
.pt-row-anim { animation: ptFadeUp 0.35s ease both; }
.pt-pop      { animation: ptPop 0.4s cubic-bezier(.175,.885,.32,1.275) both; }
.pt-bar-anim { animation: ptBarFill 0.8s ease both; }
`;

// ── STATUS BADGE ──────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.text }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.dot }} />
      {cfg.label}
    </span>
  );
};

// ── STAGE PROGRESS BAR ────────────────────────────────────────────────────────

const StageBar: React.FC<{ stages: PlacementStage[] }> = ({ stages }) => {
  const done = stages.filter(s => s.status === 'completed').length;
  const pct  = Math.round((done / 7) * 100);
  const color = pct === 100 ? '#22c55e' : pct >= 60 ? '#F0B429' : pct > 0 ? '#3b82f6' : '#e5e7eb';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex gap-0.5">
        {stages.map(st => {
          const cfg = STATUS_CFG[st.status] || STATUS_CFG.pending;
          return (
            <div key={st.stageIndex}
              title={`${st.stageName}: ${cfg.label}`}
              className="w-3.5 h-3.5 rounded-sm flex items-center justify-center transition-all"
              style={{ background: cfg.dot }}>
              {st.status === 'completed' && (
                <svg className="w-2 h-2 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5"/>
                </svg>
              )}
              {st.status === 'failed' && (
                <svg className="w-2 h-2 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l6 6M9 3l-6 6"/>
                </svg>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden min-w-[40px]">
        <div className="h-full rounded-full pt-bar-anim transition-all duration-700"
          style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-bold whitespace-nowrap" style={{ color }}>{done}/7</span>
    </div>
  );
};

// ── STAT TILE ─────────────────────────────────────────────────────────────────

const Tile: React.FC<{ label: string; value: number | string; color: string; bg: string; icon: string; delay?: number }> =
  ({ label, value, color, bg, icon, delay = 0 }) => (
  <div className="pt-pop rounded-2xl p-4 flex flex-col gap-1 shadow-sm"
    style={{ background: bg, animationDelay: `${delay}ms` }}>
    <div className="flex items-center justify-between">
      <span className="text-2xl">{icon}</span>
      <span className="text-2xl font-black" style={{ color }}>{value}</span>
    </div>
    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color, opacity: 0.7 }}>{label}</span>
  </div>
);

// ── ADD STUDENT MODAL ─────────────────────────────────────────────────────────

const AddStudentModal: React.FC<{
  adminPassword: string;
  existingIds: Set<string>;
  onClose: () => void;
  onDone: () => void;
}> = ({ adminPassword, existingIds, onClose, onDone }) => {
  const [students, setStudents] = useState<StudentFull[]>([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding]     = useState(false);
  const [progress, setProgress] = useState('');

  useEffect(() => {
    getStudents(adminPassword)
      .then(r => setStudents((r.students ?? []).filter(s => s.status === 'Active')))
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [adminPassword]);

  const filtered = students.filter(s => {
    if (existingIds.has(s.studentId)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) || s.course.toLowerCase().includes(q) || s.studentId.toLowerCase().includes(q);
  });

  const toggle = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => selected.size === filtered.length ? setSelected(new Set()) : setSelected(new Set(filtered.map(s => s.studentId)));

  const handleAddAll = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    const toAdd = filtered.filter(s => selected.has(s.studentId));
    let done = 0, failed = 0;
    for (const s of toAdd) {
      setProgress(`Adding ${s.name}… (${done + 1}/${toAdd.length})`);
      try { await addStudentToPlacement(adminPassword, s.studentId); done++; } catch { failed++; }
    }
    setAdding(false);
    if (done > 0) toast.success(`Added ${done} student${done !== 1 ? 's' : ''} to Placement Tracker`);
    if (failed > 0) toast.error(`${failed} failed`);
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative rounded-2xl shadow-2xl w-full max-w-lg z-10 flex flex-col max-h-[88vh] overflow-hidden"
        style={{ background: '#fff' }}>
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
          <div>
            <h3 className="text-base font-bold text-white">Add to Placement Tracker</h3>
            {selected.size > 0 && <p className="text-xs mt-0.5" style={{ color: '#F0B429' }}>{selected.size} selected</p>}
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all">×</button>
        </div>

        {/* Search + select-all */}
        <div className="px-4 pt-3 pb-2 border-b border-gray-100 space-y-2">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input type="text" placeholder="Search name, course, ID…" value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400" />
          </div>
          {filtered.length > 0 && (
            <button onClick={toggleAll}
              className="text-xs font-semibold px-3 py-1 rounded-lg border transition-colors hover:bg-gray-50"
              style={{ borderColor: selected.size === filtered.length ? '#F0B429' : '#e5e7eb',
                       color: selected.size === filtered.length ? '#854d0e' : '#6b7280' }}>
              {selected.size === filtered.length ? `Deselect All` : `Select All (${filtered.length})`}
            </button>
          )}
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1 divide-y divide-gray-100">
          {loading ? <div className="p-8 text-center text-gray-400 text-sm">Loading students…</div>
          : filtered.length === 0 ? <div className="p-8 text-center text-gray-400 text-sm">No eligible students found</div>
          : filtered.map(s => {
            const checked = selected.has(s.studentId);
            return (
              <label key={s.studentId}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors select-none ${checked ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle(s.studentId)}
                  className="w-4 h-4 accent-purple-600 flex-shrink-0" />
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{ background: checked ? '#4e3a69' : '#9ca3af' }}>
                  {s.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-800 truncate">{s.name}</div>
                  <div className="text-xs text-gray-400">{s.studentId} · {s.course}</div>
                </div>
                {checked && <span className="text-xs font-bold" style={{ color: '#4e3a69' }}>✓</span>}
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 flex items-center gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleAddAll} disabled={selected.size === 0 || adding}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50 transition-all"
            style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
            {adding ? progress || 'Adding…' : `Add ${selected.size > 0 ? selected.size : ''} Student${selected.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── STAGE CARD (inside detail modal) ─────────────────────────────────────────

const StageCard: React.FC<{
  stage: PlacementStage; adminPassword: string; studentId: string;
  onSaved: (updated: PlacementStage) => void;
}> = ({ stage, adminPassword, studentId, onSaved }) => {
  const [open, setOpen]   = useState(false);
  const [form, setForm]   = useState({ status: stage.status, completedDate: stage.completedDate, projectTitle: stage.projectTitle, projectDetails: stage.projectDetails, notes: stage.notes, companyName: stage.companyName || '', interviewDate: stage.interviewDate || '', offerStatus: stage.offerStatus || '', salaryOffered: stage.salaryOffered ? String(stage.salaryOffered) : '' });
  const [saving, setSaving] = useState(false);
  const isProject = IS_PROJECT(stage.stageIndex);
  const cfg = STATUS_CFG[stage.status] || STATUS_CFG.pending;
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const update = { ...form, salaryOffered: Number(form.salaryOffered) || 0 };
      await updatePlacementStage(adminPassword, studentId, stage.stageIndex, update);
      toast.success(`${stage.stageName} updated`);
      onSaved({ ...stage, ...update } as any);
      setOpen(false);
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleCancel = () => {
    setForm({ status: stage.status, completedDate: stage.completedDate, projectTitle: stage.projectTitle, projectDetails: stage.projectDetails, notes: stage.notes, companyName: stage.companyName || '', interviewDate: stage.interviewDate || '', offerStatus: stage.offerStatus || '', salaryOffered: stage.salaryOffered ? String(stage.salaryOffered) : '' });
    setOpen(false);
  };

  return (
    <div className="rounded-xl overflow-hidden border transition-all duration-200"
      style={{ borderColor: open ? cfg.ring : '#e5e7eb' }}>
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors select-none"
        onClick={() => setOpen(o => !o)}>
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white"
          style={{ background: cfg.dot }}>
          {stage.stageIndex + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-800 truncate">{stage.stageName}</div>
          {isProject && stage.projectTitle && <div className="text-xs text-gray-400 truncate">{stage.projectTitle}</div>}
          {stage.companyName && <div className="text-xs text-blue-500 truncate">🏢 {stage.companyName}{stage.offerStatus ? ` · ${stage.offerStatus}` : ''}{stage.salaryOffered ? ` · ₹${(stage.salaryOffered/100000).toFixed(1)}L` : ''}</div>}
        </div>
        <StatusBadge status={stage.status} />
        {stage.completedDate && <span className="text-xs text-gray-400 hidden sm:block">{stage.completedDate}</span>}
        <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </div>
      {open && (
        <div className="border-t bg-gray-50 px-4 py-4 space-y-3" style={{ borderColor: '#f3f4f6' }}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 bg-white">
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Date Completed</label>
              <input type="date" value={form.completedDate} onChange={e => set('completedDate', e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400" />
            </div>
          </div>
          {isProject && (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Project Title</label>
                <input type="text" value={form.projectTitle} onChange={e => set('projectTitle', e.target.value)}
                  placeholder="e.g. Student Management System"
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Project Details</label>
                <textarea value={form.projectDetails} onChange={e => set('projectDetails', e.target.value)} rows={3}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 resize-none" />
              </div>
            </>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 resize-none" />
          </div>
          {/* Interview / Offer tracking */}
          <div className="rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/40 p-3 space-y-3">
            <p className="text-xs font-bold text-blue-700 uppercase tracking-wide">🏢 Interview & Offer Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Company Name</label>
                <input type="text" value={form.companyName} onChange={e => set('companyName', e.target.value)}
                  placeholder="Company name"
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 bg-white" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Interview Date</label>
                <input type="date" value={form.interviewDate} onChange={e => set('interviewDate', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 bg-white" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Offer Status</label>
                <select value={form.offerStatus} onChange={e => set('offerStatus', e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 bg-white">
                  <option value="">— None —</option>
                  <option value="Offered">Offered</option>
                  <option value="Accepted">Accepted</option>
                  <option value="Negotiating">Negotiating</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Salary Offered (₹)</label>
                <input type="number" value={form.salaryOffered} onChange={e => set('salaryOffered', e.target.value)}
                  placeholder="e.g. 300000"
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 bg-white" />
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button onClick={handleCancel} className="px-4 py-2 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
              {saving ? 'Saving…' : 'Save Stage'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── STUDENT DETAIL MODAL ──────────────────────────────────────────────────────

const StudentDetailModal: React.FC<{
  student: PlacementStudent; adminPassword: string;
  onClose: () => void; onUpdate: (u: PlacementStudent) => void; onRemove: () => void;
}> = ({ student, adminPassword, onClose, onUpdate, onRemove }) => {
  const [stages, setStages] = useState<PlacementStage[]>(student.stages);
  const [removing, setRemoving] = useState(false);
  const done   = stages.filter(s => s.status === 'completed').length;
  const allDone = done === 7;
  const pct    = Math.round((done / 7) * 100);

  const handleStageUpdate = (updated: PlacementStage) => {
    const next = stages.map(s => s.stageIndex === updated.stageIndex ? updated : s);
    setStages(next);
    onUpdate({ ...student, stages: next });
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${student.studentName} from Placement Tracker?`)) return;
    setRemoving(true);
    try {
      await removeStudentFromPlacement(adminPassword, student.studentId);
      toast.success(`${student.studentName} removed`);
      onRemove();
    } catch (e: any) { toast.error(e.message); }
    finally { setRemoving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative rounded-2xl shadow-2xl w-full max-w-xl z-10 flex flex-col max-h-[92vh] overflow-hidden bg-white pt-pop">
        {/* Header */}
        <div className="px-5 pt-5 pb-4 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg,#1e1730,#2b2240,#3d2f5e)' }}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-sm font-black"
                style={{ background: '#F0B429', color: '#2b2240' }}>
                {student.studentName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <h3 className="text-base font-bold text-white">{student.studentName}</h3>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{student.studentId} · {student.course || '—'}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-7 h-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all mt-1">×</button>
          </div>
          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, background: allDone ? '#22c55e' : '#F0B429' }} />
            </div>
            <span className="text-xs font-bold whitespace-nowrap"
              style={{ color: allDone ? '#22c55e' : '#F0B429' }}>
              {done}/7 {allDone ? '🎓 Placed!' : 'stages'}
            </span>
          </div>
        </div>

        {/* Stage cards */}
        <div className="overflow-y-auto flex-1 px-4 py-4 space-y-2">
          {/* Their task scores (Tasks & Reviews): how they did on the work their mentor reviewed */}
          {!!student.taskScore?.reviewed && (
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50/60 px-4 py-3 mb-2 flex items-center gap-3">
              <span className="text-2xl font-black text-amber-700">{student.taskScore.avgScore}</span>
              <div className="min-w-0">
                <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">📝 Task score (out of 10)</p>
                <p className="text-[11px] text-gray-600">
                  {student.taskScore.reviewed} reviewed · {student.taskScore.done} of {student.taskScore.given} accepted
                  {student.taskScore.onTimePct !== null ? ` · ${student.taskScore.onTimePct}% on time` : ''}{student.taskScore.missed ? ` · ${student.taskScore.missed} missed` : ''}
                </p>
              </div>
            </div>
          )}
          {(student.jobOffers ?? []).length > 0 && (
            <div className="rounded-xl border-2 border-green-200 bg-green-50/60 px-4 py-3 mb-2">
              <p className="text-xs font-bold text-green-800 uppercase tracking-wide mb-1.5">💼 Offers from the Job Board</p>
              {(student.jobOffers ?? []).map(o => (
                <div key={o.jobId} className="flex items-start justify-between gap-3 py-1">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{o.company ? `${o.company} · ` : ''}{o.jobTitle}</p>
                    <p className="text-[11px] text-gray-500">
                      Recorded{o.byName ? ` by ${o.byName}` : ''}{o.at ? ` · ${new Date(o.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}` : ''}
                    </p>
                  </div>
                  <span className="text-xs font-bold text-green-700 whitespace-nowrap">{o.salary ? `${lakhs(o.salary)} a year` : 'Salary not added'}</span>
                </div>
              ))}
            </div>
          )}
          {stages.map(stage => (
            <StageCard key={stage.stageIndex} stage={stage} adminPassword={adminPassword}
              studentId={student.studentId} onSaved={handleStageUpdate} />
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 flex justify-between items-center flex-shrink-0">
          <button onClick={handleRemove} disabled={removing}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border-2 disabled:opacity-50 transition-colors"
            style={{ borderColor: '#dc2626', color: '#dc2626' }}>
            {removing ? 'Removing…' : 'Remove from Tracker'}
          </button>
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold border-2 border-gray-200 text-gray-600 hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── INACTIVE STUDENT TABLE (read-only) ───────────────────────────────────────

const InactiveTable: React.FC<{ students: PlacementStudent[]; inactiveSearch: string }> = ({ students, inactiveSearch }) => {
  const visible = students.filter(s => {
    if (!inactiveSearch) return true;
    const q = inactiveSearch.toLowerCase();
    return (
      s.studentName.toLowerCase().includes(q) ||
      (s.course || '').toLowerCase().includes(q) ||
      s.studentId.toLowerCase().includes(q)
    );
  });

  if (students.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center">
        <div className="text-5xl mb-4">✅</div>
        <p className="font-bold text-gray-600">No deactivated students in the tracker</p>
        <p className="text-xs text-gray-400 mt-1">All placement records belong to active students.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-2" style={{ background: '#fef3c7', borderBottom: '1px solid #fde68a' }}>
        <span className="text-sm">⚠️</span>
        <p className="text-xs font-semibold text-amber-800">
          These students are deactivated — their records are shown for reference only. All actions are disabled.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr style={{ background: '#78716c' }}>
              {['#', 'Student', 'Status', 'Course', 'Progress at Deactivation', 'Last Stage'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((s, idx) => {
              const done = completedCount(s);
              const current = currentStageOf(s);
              const statusLabel = s.studentStatus || 'Inactive';
              const statusColor = statusLabel === 'Ran Away' ? '#b91c1c' : statusLabel === 'Institute Leave' ? '#92400e' : '#6b7280';
              const statusBg   = statusLabel === 'Ran Away' ? '#fee2e2' : statusLabel === 'Institute Leave' ? '#fef9c3' : '#f3f4f6';
              return (
                <tr key={s.studentId}
                  className="pt-row-anim border-b border-gray-100 opacity-70"
                  style={{ animationDelay: `${Math.min(idx * 25, 250)}ms`, background: '#fafaf9' }}>
                  <td className="px-4 py-3 text-xs text-gray-300 font-mono">{idx + 1}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black text-white flex-shrink-0 bg-gray-400">
                        {s.studentName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-500 line-through whitespace-nowrap">{s.studentName}</div>
                        <div className="text-xs text-gray-400">{s.studentId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
                      style={{ background: statusBg, color: statusColor }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
                      {statusLabel}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-lg">{s.course || '—'}</span>
                  </td>
                  <td className="px-4 py-3 min-w-[180px]">
                    <StageBar stages={s.stages} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-400 whitespace-nowrap">{done === 7 ? '🎓 All Stages Done' : current}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────

const PlacementTracker: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [students, setStudents]         = useState<PlacementStudent[]>([]);
  const [inactiveStudents, setInactive] = useState<PlacementStudent[]>([]);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState<'active' | 'inactive'>('active');
  const [search, setSearch]             = useState('');
  const [inactiveSearch, setInactiveSearch] = useState('');
  const [filterCourse, setFilterCourse]   = useState('');
  const [filterStage, setFilterStage]     = useState('');
  const [filterProgress, setFilterProgress] = useState('');
  const [showAdd, setShowAdd]     = useState(false);
  const [selected, setSelected]   = useState<PlacementStudent | null>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    if (styleRef.current) return;
    const el = document.createElement('style');
    el.textContent = CSS;
    document.head.appendChild(el);
    styleRef.current = el;
    return () => { el.remove(); styleRef.current = null; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPlacementData(adminPassword);
      setStudents(res.students ?? []);
      setInactive(res.inactiveStudents ?? []);
    } catch (e: any) { toast.error('Failed to load: ' + e.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  const existingIds = new Set([...students, ...inactiveStudents].map(s => s.studentId));

  // Derived filter options
  const courses = Array.from(new Set(students.map(s => s.course).filter(Boolean))).sort();

  const visible = students.filter(s => {
    const q = search.toLowerCase();
    if (q && !(
      s.studentName.toLowerCase().includes(q) ||
      s.course.toLowerCase().includes(q) ||
      s.studentId.toLowerCase().includes(q)
    )) return false;
    if (filterCourse && s.course !== filterCourse) return false;
    if (filterStage) {
      const cur = currentStageOf(s);
      if (cur !== filterStage && !(filterStage === 'All Complete' && completedCount(s) === 7)) return false;
    }
    if (filterProgress) {
      const done = completedCount(s);
      const failed = hasFailed(s);
      if (filterProgress === 'not_started' && done !== 0) return false;
      if (filterProgress === 'in_progress' && (done === 0 || done === 7)) return false;
      if (filterProgress === 'placed' && done !== 7) return false;
      if (filterProgress === 'has_failed' && !failed) return false;
    }
    return true;
  });

  const totalPlaced    = students.filter(s => completedCount(s) === 7).length;
  const totalInProgress = students.filter(s => { const d = completedCount(s); return d > 0 && d < 7; }).length;
  const totalFailed    = students.filter(hasFailed).length;

  const handleUpdate = (updated: PlacementStudent) => {
    setStudents(prev => prev.map(s => s.studentId === updated.studentId ? updated : s));
    if (selected?.studentId === updated.studentId) setSelected(updated);
  };
  const handleRemove = (studentId: string) => { setStudents(prev => prev.filter(s => s.studentId !== studentId)); setSelected(null); };

  const hasFilters = search || filterCourse || filterStage || filterProgress;

  return (
    <TrackerLayout>
      <div className="space-y-5 max-w-7xl mx-auto">

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black" style={{ color: '#2b2240' }}>Placement Tracker</h1>
            <p className="text-xs text-gray-400 mt-0.5">Track students through 7 interview + project stages</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {activeTab === 'active' && (
              <button onClick={() => setShowAdd(true)}
                className="px-4 py-2 rounded-xl text-sm font-bold text-white shadow-sm transition-all hover:opacity-90 active:scale-95"
                style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }}>
                + Add Student
              </button>
            )}
            <button onClick={load}
              className="px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
              Refresh
            </button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex gap-1 p-1 rounded-xl bg-gray-100 w-fit">
          <button
            onClick={() => setActiveTab('active')}
            className="px-4 py-2 rounded-lg text-sm font-bold transition-all"
            style={activeTab === 'active'
              ? { background: '#2b2240', color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }
              : { background: 'transparent', color: '#6b7280' }}>
            Active Students
            <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
              style={{ background: activeTab === 'active' ? 'rgba(255,255,255,0.2)' : '#e5e7eb', color: activeTab === 'active' ? '#fff' : '#6b7280' }}>
              {students.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('inactive')}
            className="px-4 py-2 rounded-lg text-sm font-bold transition-all"
            style={activeTab === 'inactive'
              ? { background: '#78716c', color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }
              : { background: 'transparent', color: '#6b7280' }}>
            Deactivated
            {inactiveStudents.length > 0 && (
              <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
                style={{ background: activeTab === 'inactive' ? 'rgba(255,255,255,0.25)' : '#fde68a', color: activeTab === 'inactive' ? '#fff' : '#92400e' }}>
                {inactiveStudents.length}
              </span>
            )}
          </button>
        </div>

        {/* ── INACTIVE TAB ── */}
        {activeTab === 'inactive' && !loading && (
          <>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input type="text" placeholder="Search deactivated students…"
                value={inactiveSearch} onChange={e => setInactiveSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 transition-colors bg-white" />
            </div>
            <InactiveTable students={inactiveStudents} inactiveSearch={inactiveSearch} />
          </>
        )}

        {/* ── ACTIVE TAB ── */}
        {activeTab === 'active' && (
          <>
            {/* Stat tiles */}
            {!loading && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Tile label="Total"       value={students.length}  color="#2b2240" bg="#f0edf7" icon="👥" delay={0} />
                <Tile label="In Progress" value={totalInProgress}  color="#1d4ed8" bg="#eff6ff" icon="⚡" delay={60} />
                <Tile label="Has Failed"  value={totalFailed}      color="#b91c1c" bg="#fee2e2" icon="⚠️" delay={120} />
                <Tile label="Placed 🎓"   value={totalPlaced}      color="#15803d" bg="#dcfce7" icon="🏆" delay={180} />
              </div>
            )}

            {/* Stage legend */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">7 Placement Stages</p>
              <div className="flex flex-wrap gap-2">
                {STAGES.map((name, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-50 border border-gray-200 text-gray-600 cursor-pointer transition-all hover:border-yellow-400 hover:text-yellow-700"
                    onClick={() => setFilterStage(filterStage === name ? '' : name)}>
                    <span className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[10px] font-black flex-shrink-0"
                      style={{ background: filterStage === name ? '#F0B429' : '#2b2240' }}>
                      {i + 1}
                    </span>
                    {name}
                  </span>
                ))}
              </div>
            </div>

            {/* Filter bar */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col gap-3">
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <input type="text" placeholder="Search by name, course, student ID…"
                  value={search} onChange={e => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-yellow-400 transition-colors" />
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Filter:</span>
                <select value={filterCourse} onChange={e => setFilterCourse(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 min-w-[110px]">
                  <option value="">All Courses</option>
                  {courses.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={filterProgress} onChange={e => setFilterProgress(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 min-w-[130px]">
                  <option value="">All Progress</option>
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="placed">Placed ✓</option>
                  <option value="has_failed">Has Failed</option>
                </select>
                <select value={filterStage} onChange={e => setFilterStage(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 min-w-[150px]">
                  <option value="">All Stages</option>
                  {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                  <option value="All Complete">All Complete</option>
                </select>
                {hasFilters && (
                  <button
                    onClick={() => { setSearch(''); setFilterCourse(''); setFilterStage(''); setFilterProgress(''); }}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-200 text-red-500 bg-red-50 hover:bg-red-100 transition-colors">
                    Clear Filters
                  </button>
                )}
                <span className="ml-auto text-xs text-gray-400">{visible.length} / {students.length} students</span>
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className="bg-white rounded-2xl border border-gray-100 py-20 flex flex-col items-center gap-3 text-gray-400">
                <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Loading placement data…</span>
              </div>
            ) : visible.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
                <div className="text-5xl mb-4">📋</div>
                <p className="font-bold text-gray-600">{students.length === 0 ? 'No students in placement tracker yet' : 'No students match your filters'}</p>
                <p className="text-xs text-gray-400 mt-1">{students.length === 0 ? 'Click "+ Add Student" to begin.' : 'Try clearing your filters.'}</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg,#1e1730,#2b2240)' }}>
                        {['#', 'Student', 'Course', 'Progress', 'Current Stage', 'Action'].map(h => (
                          <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-white/80 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((s, idx) => {
                        const done   = completedCount(s);
                        const allDone = done === 7;
                        const failed = hasFailed(s);
                        const current = currentStageOf(s);
                        return (
                          <tr key={s.studentId}
                            className="pt-row-anim border-b border-gray-50 hover:bg-yellow-50/30 transition-colors cursor-pointer group"
                            style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                            onClick={() => setSelected(s)}>
                            <td className="px-4 py-3.5 text-xs text-gray-300 font-mono">{idx + 1}</td>
                            <td className="px-4 py-3.5">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black text-white flex-shrink-0"
                                  style={{ background: allDone ? '#15803d' : failed ? '#b91c1c' : '#2b2240' }}>
                                  {s.studentName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                </div>
                                <div>
                                  <div className="font-bold text-gray-800 whitespace-nowrap">{s.studentName}</div>
                                  <div className="text-xs text-gray-400">{s.studentId}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3.5">
                              <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-lg whitespace-nowrap">{s.course || '—'}</span>
                            </td>
                            <td className="px-4 py-3.5 min-w-[180px]">
                              <StageBar stages={s.stages} />
                            </td>
                            <td className="px-4 py-3.5">
                              {allDone ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                                  🎓 Placed
                                </span>
                              ) : (
                                <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{current}</span>
                              )}
                              {offerSummary(s) && (
                                <div className="mt-1">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-700 border border-green-200 whitespace-nowrap">
                                    💼 {offerSummary(s)}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3.5">
                              <button
                                onClick={e => { e.stopPropagation(); setSelected(s); }}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold border-2 whitespace-nowrap transition-all group-hover:scale-105"
                                style={{ borderColor: '#F0B429', color: '#2b2240', background: '#fefce8' }}>
                                View &amp; Edit
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showAdd && (
        <AddStudentModal adminPassword={adminPassword} existingIds={existingIds}
          onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />
      )}
      {selected && (
        <StudentDetailModal student={selected} adminPassword={adminPassword}
          onClose={() => setSelected(null)} onUpdate={handleUpdate}
          onRemove={() => handleRemove(selected.studentId)} />
      )}
    </TrackerLayout>
  );
};

export default PlacementTracker;
